import type { HTMLChakraProps } from '@chakra-ui/react';
import { forwardRef, useMultiStyleConfig } from '@chakra-ui/react';
import { chakra } from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';

import { ThinkingProvider } from '../ThinkingContext';
import { ThinkingStyles, ThinkingStylesProvider } from '../useThinkingStyles';

export type ThinkingProps = HTMLChakraProps<'div'> & {
  isThinking?: boolean;
  isToggleExpansionDisabled?: boolean;
  isExpanded?: boolean;
  setIsExpanded?: (value: boolean) => void;
};

/**
 * Thinking helps communicate the current status, active activity, or past activity of an agent.
 */
export const Thinking: React.FC<ThinkingProps> = forwardRef<
  ThinkingProps,
  'div'
>(
  (
    {
      isThinking = true,
      isToggleExpansionDisabled = false,
      isExpanded: isExpandedProp,
      setIsExpanded: setIsExpandedProp,
      children,
      ...props
    },
    ref,
  ) => {
    const thinkingStyles = useMultiStyleConfig('Thinking') as ThinkingStyles;

    // Allow internal or external state management as appropriate.
    if (isExpandedProp == null && setIsExpandedProp != null) {
      throw new Error(
        '`<Thinking/>` must be supplied IsExpanded when setIsExpanded is already provided to be used in fully controlled mode.',
      );
    }
    const [internalExpanded, setInternalExpanded] = useState(false);
    const isExpanded = useCallback(
      () => isExpandedProp ?? internalExpanded,
      [internalExpanded, isExpandedProp],
    );
    const setIsExpanded = useMemo(
      () =>
        setIsExpandedProp != null ? setIsExpandedProp : setInternalExpanded,
      [setIsExpandedProp],
    );

    return (
      <ThinkingProvider
        value={{
          isThinking,
          isExpanded: isExpanded(),
          setIsExpanded,
          isToggleExpansionDisabled,
        }}
      >
        <ThinkingStylesProvider value={thinkingStyles}>
          <chakra.div
            sx={thinkingStyles.container}
            className="thinking-container"
            {...props}
            ref={ref}
          >
            {children}
          </chakra.div>
        </ThinkingStylesProvider>
      </ThinkingProvider>
    );
  },
);
Thinking.displayName = 'Thinking';
